import {
  Button,
  DialogFooter,
  Form,
  FormButton,
  FormSubmitHandler,
  openConfirmDialog,
  PhoneNumber,
  showNotification,
  Tooltip,
} from 'platform/components';
import {Box, HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';
import {mixed, object} from 'yup';

import {useState} from 'react';
import {SubmitErrorHandler} from 'react-hook-form';

import {equals, indexBy, isNil, isNotEmpty, isNotNil, mapObjIndexed, reject, toPairs} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  GetSignableDocumentListResponseBody,
  RemoteFileSignatureBody,
  useSignDocumentRemotelyMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError, isPhoneNumberValid} from '@dms/shared';

import {Nullish, suffixTestId, yupString} from 'shared/web';

import {BASE_PATH_SEPARATOR} from '../../../constants/BASE_PATH_SEPARATOR';
import {useDocumentValidationState} from '../../../contexts/DocumentStateProvider';
import {DefaultSignatoriesFormData} from '../../../types/DefaultSignatoriesFormData';
import {DocumentDefaultSignatoriesFormData} from '../../../types/DocumentDefaultSignatoriesFormData';
import {StepContentProps} from '../../../types/StepContentProps';
import {buildSignatoriesFormValues} from '../../../utils/buildSignatoriesFormValues';
import {getContextSessionName} from '../../../utils/getContextSessionName';
import {getDefaultSignatoriesErrors} from '../../../utils/getDefaultSignatoriesErrors';
import {getUniqueSignatories} from '../../../utils/getUniqueSignatories';
import {mergeSignatoriesFormValues} from '../../../utils/mergeSignatoriesFormValues';
import {FormValues} from '../types/FormValues';
import {DefaultSignatories} from './DefaultSignatories';
import {DocumentSignatories} from './DocumentSignatories';

interface SignatoriesSelectFormProps extends StepContentProps {
  documents: GetSignableDocumentListResponseBody[];
  initialDefaults: DefaultSignatoriesFormData;
  documentDefaults: DocumentDefaultSignatoriesFormData;
}

export function SignatoriesSelectForm(props: SignatoriesSelectFormProps) {
  const {
    activeDocumentId,
    setDocumentsValidationState,
    clearAllDocumentsValidationState,
    documentValidationState,
  } = useDocumentValidationState();

  const [signRemotely] = useSignDocumentRemotelyMutation({fixedCacheKey: 'signRemotely'});

  const [defaultsFormData, setDefaultsFormData] = useState(props.initialDefaults);
  const [lastAppliedDefaults, setLastAppliedDefaults] = useState(props.initialDefaults);
  const [haveDefaultsBeenApplied, setHaveDefaultsBeenApplied] = useState(false);

  const documentByFileId = indexBy((item) => item?.sourceFileId, props.documents);
  const activeDocument = documentByFileId[activeDocumentId ?? ''];

  const uniqueSignatories = getUniqueSignatories(props.documents);
  const signatoryPersons = uniqueSignatories.map((signatory) => signatory.signatoryPerson);

  const areDefaultsDirty = !equals(defaultsFormData, lastAppliedDefaults);
  const defaultsErrors = getDefaultSignatoriesErrors(defaultsFormData, signatoryPersons);
  const hasInvalidDefaults = isNotNilOrEmpty(defaultsErrors);

  const seededFormValues = buildSignatoriesFormValues(
    props.documents,
    props.initialDefaults,
    props.documentDefaults
  );

  const handleSubmit: FormSubmitHandler<FormValues> = async (data) => {
    const files = toPairs(data).reduce<RemoteFileSignatureBody[]>((prev, [id], _, array) => {
      const fileId = id.split(BASE_PATH_SEPARATOR)[0];

      if (prev.some((item) => item.fileId === fileId)) {
        return prev;
      }

      const allSignatories = array
        .filter(([key]) => key.startsWith(fileId))
        .map(([signatoryKey, signatoryValue]) => {
          const signatoryId = signatoryKey.split(BASE_PATH_SEPARATOR)[1];

          return {
            ...signatoryValue,
            id: signatoryId,
          };
        });

      return [...prev, {fileId, signatoryPersons: allSignatories}];
    }, []);

    await signRemotely({
      sessionName: getContextSessionName(props.context, props.documents.length),
      files,
    })
      .unwrap()
      .then(() => {
        props.setSigningStep('Remote_sentToSignatories');
        clearAllDocumentsValidationState();
      })
      .catch((error: Error) => {
        handleApiError(error, {silent: true});
        props.setSigningStep('Remote_error');
      })
      .finally(props.refreshData);
  };

  const handleInvalidSubmit: SubmitErrorHandler<FormValues> = (errors) => {
    const fileIdsWithErrors = Object.keys(errors)
      .map((key) => key.split(BASE_PATH_SEPARATOR)[0])
      .filter((fileId) => fileId !== activeDocumentId);

    setDocumentsValidationState(fileIdsWithErrors);

    if (isNotEmpty(fileIdsWithErrors)) {
      showNotification.error(
        i18n.t('entity.document.notifications.documentsWithInvalidSignatories')
      );
    }
  };

  return (
    <Form<FormValues>
      schema={getSchema(seededFormValues)}
      defaultValues={seededFormValues}
      onSubmit={handleSubmit}
      onInvalidSubmit={handleInvalidSubmit}
      shouldHideInvalidSubmitNotification
    >
      {(control, formApi) => {
        const submitForm = () => {
          control.submitForm();
        };

        const applyToAllDocuments = (
          buildFormValues: () => ReturnType<typeof buildSignatoriesFormValues>,
          onApplied: () => void
        ) => {
          const applyValues = () => {
            formApi.reset(buildFormValues());
            setLastAppliedDefaults(defaultsFormData);
            setHaveDefaultsBeenApplied(true);
            clearAllDocumentsValidationState();
            onApplied();
          };

          const lastAppliedFormValues = haveDefaultsBeenApplied
            ? buildSignatoriesFormValues(props.documents, lastAppliedDefaults)
            : seededFormValues;
          const hasManualDocumentEdits = !equals(
            reject(isNil, formApi.getValues()),
            reject(isNil, lastAppliedFormValues)
          );

          match(hasManualDocumentEdits)
            .with(true, () =>
              openConfirmDialog({
                text: i18n.t('entity.document.labels.applyDefaultValuesConfirm'),
                onConfirm: applyValues,
              })
            )
            .otherwise(applyValues);
        };

        const handleApply = () =>
          applyToAllDocuments(
            () =>
              mergeSignatoriesFormValues(props.documents, defaultsFormData, formApi.getValues()),
            () =>
              showNotification.success(i18n.t('entity.document.labels.defaultSignatoriesApplied'))
          );

        const handleApplyAndContinue = () =>
          match(areDefaultsDirty)
            .with(true, () =>
              applyToAllDocuments(
                () => buildSignatoriesFormValues(props.documents, defaultsFormData),
                submitForm
              )
            )
            .otherwise(submitForm);

        const submitButton = isNil(activeDocument) ? (
          <HStack spacing={2}>
            <Button
              title={i18n.t('entity.document.actions.applyToAllDocuments')}
              variant="secondary"
              onClick={handleApply}
              data-testid={suffixTestId('applyToAllDocuments', props)}
            />

            <Tooltip
              label={i18n.t('entity.document.labels.allSignatoryDetailsRequiredToContinue')}
              isDisabled={!hasInvalidDefaults}
              placement="top"
            >
              <Button
                title={i18n.t('entity.document.actions.applyToAllDocumentsAndContinue')}
                variant="primary"
                onClick={handleApplyAndContinue}
                isLoading={formApi.formState.isSubmitting}
                isDisabled={hasInvalidDefaults}
                data-testid={suffixTestId('applyToAllDocumentsAndContinue', props)}
              />
            </Tooltip>
          </HStack>
        ) : (
          <FormButton
            control={control}
            type="submit"
            title={i18n.t('general.actions.continue')}
            variant="primary"
            isDisabled={isNotEmpty(documentValidationState)}
            data-testid={suffixTestId('continue', props)}
          />
        );

        return (
          <>
            <Show when={isNotNil(activeDocument)}>
              <DocumentSignatories
                control={control}
                formApi={formApi}
                activeDocument={activeDocument}
                key={activeDocument?.sourceFileId}
              />
            </Show>

            <Show when={isNil(activeDocument)}>
              <DefaultSignatories
                signatories={uniqueSignatories}
                formData={defaultsFormData}
                onFormDataChange={setDefaultsFormData}
                errors={defaultsErrors}
                data-testid={suffixTestId('defaultSignatories', props)}
              />
            </Show>

            <DialogFooter>
              <Box paddingHorizontal={0}>
                <HStack justify="space-between">
                  <Button
                    title={i18n.t('entity.document.actions.goToModeSelect')}
                    variant="dangerOutlined"
                    onClick={() => {
                      clearAllDocumentsValidationState();
                      props.setSigningStep('modeSelect');
                    }}
                    data-testid={suffixTestId('modeSelect', props)}
                  />

                  {submitButton}
                </HStack>
              </Box>
            </DialogFooter>
          </>
        );
      }}
    </Form>
  );
}

const phoneNumberSchema = mixed()
  .test(
    'isPhone',
    i18n.t('general.validations.invalidPhoneNumber'),
    (phone?: PhoneNumber | Nullish) => {
      if (isNilOrEmpty(phone?.number)) {
        return true;
      }
      return isPhoneNumberValid(phone);
    }
  )
  .test(
    'required',
    i18n.t('general.validations.fieldIsRequired'),
    (phone?: PhoneNumber | Nullish) => isNotNilOrEmpty(phone?.number)
  );

const getSchema = (obj: ReturnType<typeof buildSignatoriesFormValues>) =>
  object(
    mapObjIndexed(
      () =>
        object({
          email: yupString.required(),
          name: yupString.required(),
          phoneNumber: phoneNumberSchema,
        }),
      obj
    )
  );
